import { useCallback, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../constants/routes';
import { StationFormWizard } from './components/StationFormWizard';
import { INITIAL_FORM_DATA, formDataToPayload, type StationFormData } from './stationForm';
import { ocppService } from '../../../services/ocpp/OcppService';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import { usePermissions } from '../../../hooks/usePermissions';
import './create-station.css';

export const CreateStation = memo(function CreateStation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { has, organization } = usePermissions();
  // Assigning a station to an organization is platform-admin only.
  const isAdmin = has('organizations.manage') && !organization;
  const redirectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const handleCancel = useCallback(() => {
    navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_STATIONS}`);
  }, [navigate]);

  const handleSubmit = useCallback(async (formData: StationFormData) => {
    const payload = formDataToPayload(formData, isAdmin, 'create');
    await ocppService.createStation({
      idTag: formData.idTag,
      ...payload,
      organizationId: payload.organizationId ?? undefined,
    });

    queryClient.invalidateQueries({ queryKey: [DashboardQueryKey.STATIONS] });
    queryClient.invalidateQueries({ queryKey: [DashboardQueryKey.STATION_OPTIONS] });
    redirectTimerRef.current = window.setTimeout(() => {
      navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_STATIONS}`);
    }, 2000);
  }, [navigate, queryClient, isAdmin]);

  const getErrorMessage = useCallback((err: unknown) => {
    const errorObj = err as { response?: { data?: { message?: string } } };
    return errorObj?.response?.data?.message || t('dashboard.createStationV2.errorCreating');
  }, [t]);

  return (
    <div className="create-station-page">
      {/* Header */}
      <header className="create-station-header">
        <button
          type="button"
          className="back-button"
          onClick={handleCancel}
        >
          <span className="material-symbols-outlined">arrow_back</span>
          <span>{t('dashboard.createStationV2.backToStations')}</span>
        </button>
        <div className="header-divider" />
        <h1 className="page-title">{t('dashboard.createStationV2.title')}</h1>
      </header>

      <StationFormWizard
        mode="create"
        initialData={INITIAL_FORM_DATA}
        contentClassName="create-station-content"
        onSubmit={handleSubmit}
        getErrorMessage={getErrorMessage}
        onCancel={handleCancel}
      />
    </div>
  );
});

export default CreateStation;
